import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { fmtINR, fmtLakh } from "../lib/format";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { Activity, Wifi, Server, TrendingUp } from "lucide-react";

const COLORS = ["#1E3A8A", "#2563EB", "#0891B2", "#0D9488", "#65A30D", "#CA8A04", "#EA580C", "#DC2626", "#9333EA", "#DB2777"];

function Stat({ label, value, sub, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-[11px] uppercase font-bold text-slate-500">{label}</div>
      <div className={`font-head text-3xl font-extrabold mt-1 ${accent || ""}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

async function downloadCsv(url, filename) {
  try {
    const { data } = await api.get(url, { responseType: "blob" });
    const objUrl = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = objUrl; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
  } catch { /* ignore */ }
}

function IllMatchReport() {
  const [rep, setRep] = useState(null);
  useEffect(() => { api.get("/reports/ill-match").then((r) => setRep(r.data)).catch(() => {}); }, []);
  if (!rep?.by_isp?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 lg:col-span-2">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-head font-bold flex items-center gap-2"><Wifi size={18} className="text-blue-700" /> ILL bills auto-matched vs Needs-Cust-ID, by ISP</h3>
        <button onClick={() => downloadCsv("/reports/ill-match.csv", "ill-match-by-isp.csv")} className="text-xs text-blue-700 font-medium">Export CSV</button>
      </div>
      <p className="text-sm text-slate-500 mb-3">Low auto-match % = push that ISP to print a link ID on their bills.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase text-slate-500 border-b">
            <th className="py-2 pr-3">ISP</th><th className="py-2 px-3">Bills</th><th className="py-2 px-3">Auto-matched</th>
            <th className="py-2 px-3">Needs Cust ID</th><th className="py-2 px-3">Orphan</th>
          </tr></thead>
          <tbody>
            {rep.by_isp.map((r) => (
              <tr key={r.isp} className="border-b border-slate-100">
                <td className="py-2 pr-3 font-medium">{r.isp}</td>
                <td className="py-2 px-3">{r.total}</td>
                <td className="py-2 px-3"><span className={r.auto_pct >= 70 ? "text-emerald-700" : r.auto_pct >= 40 ? "text-amber-700" : "text-rose-700"}>{r.auto_pct}%</span> <span className="text-slate-400">({r.auto})</span></td>
                <td className="py-2 px-3">{r.needs_pct}% <span className="text-slate-400">({r.needs_cust_id})</span></td>
                <td className="py-2 px-3">{r.orphan_pct}% <span className="text-slate-400">({r.orphan})</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Management() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard/management").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="text-slate-400">Loading…</div>;
  const t = d.totals, r = d.renewals, c = d.compliance;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-head text-2xl font-extrabold flex items-center gap-2"><Activity className="text-blue-700" /> Management Dashboard</h1>
        <p className="text-slate-500 text-sm mt-1">Portfolio health, cash requirements and renewal compliance at a glance.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Active Circuits" value={t.active_circuits} />
        <Stat label="Broadband" value={t.broadband} />
        <Stat label="ILL" value={t.ill} />
        <Stat label="Prepaid" value={t.prepaid} />
        <Stat label="Postpaid" value={t.postpaid} />
        <Stat label="Verify Pending" value={t.verification_pending} accent="text-emerald-600" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Overdue" value={r.overdue.count} sub={fmtINR(r.overdue.amount)} accent="text-red-600" />
        <Stat label="Due Today" value={r.today.count} sub={fmtINR(r.today.amount)} accent="text-rose-600" />
        <Stat label="Next 7 Days" value={r.next_7.count} sub={fmtLakh(r.next_7.amount) + " required"} />
        <Stat label="Next 30 Days" value={r.next_30.count} sub={fmtLakh(r.next_30.amount) + " required"} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Completed This Month" value={c.completed_this_month} />
        <Stat label="On-time %" value={`${c.ontime_pct}%`} accent="text-emerald-600" />
        <Stat label="Late This Month" value={c.late_this_month} accent="text-red-600" />
        <Stat label="Avg Days Late" value={c.avg_days_late} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-head font-bold mb-4 flex items-center gap-2"><TrendingUp size={16} /> Renewals & Cash by Month</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={d.charts.by_month}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis yAxisId="l" tick={{ fontSize: 11 }} /><YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v, n) => n === "amount" ? fmtINR(v) : v} />
              <Bar yAxisId="l" dataKey="renewals" fill="#2563EB" radius={[4, 4, 0, 0]} name="Renewals" />
              <Bar yAxisId="r" dataKey="amount" fill="#0D9488" radius={[4, 4, 0, 0]} name="Amount" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-head font-bold mb-4 flex items-center gap-2"><Server size={16} /> Circuits by ISP/BSO</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={d.charts.by_isp} dataKey="count" nameKey="isp" cx="50%" cy="50%" outerRadius={95} label={(e) => e.isp}>
                {d.charts.by_isp.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-head font-bold mb-4 flex items-center gap-2"><Wifi size={16} /> Broadband Connections by ISP</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={d.charts.broadband_by_isp} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis type="number" tick={{ fontSize: 11 }} /><YAxis type="category" dataKey="isp" width={110} tick={{ fontSize: 11 }} />
              <Tooltip /><Bar dataKey="count" fill="#1E3A8A" radius={[0, 4, 4, 0]} name="Broadband" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-head font-bold mb-4 flex items-center gap-2"><Activity size={16} /> Late Renewals Trend</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={d.charts.late_by_month}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip /><Line type="monotone" dataKey="late" stroke="#DC2626" strokeWidth={2.5} dot={{ r: 3 }} name="Late" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5 lg:col-span-2">
          <h3 className="font-head font-bold mb-4">Late Renewals by Responsible Employee</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d.charts.late_by_employee}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="employee" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip /><Bar dataKey="late" fill="#EA580C" radius={[4, 4, 0, 0]} name="Late renewals" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <IllMatchReport />
      </div>
    </div>
  );
}
